import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Icon,
  Card,
  CardHeader,
  CardBody,
  Stack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Progress,
  HStack,
  Button,
  Select,
  useColorModeValue,
  Spinner,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  CircularProgress,
  CircularProgressLabel,
} from '@chakra-ui/react';
import {
  FiDollarSign,
  FiArrowUpRight,
  FiArrowDownRight,
  FiCalendar,
  FiPieChart,
  FiBarChart2,
  FiTrendingUp,
  FiTrendingDown,
  FiCreditCard,
  FiCheckCircle,
  FiAlertCircle,
  FiClock,
  FiTarget,
  FiRefreshCw,
  FiUser,
  FiUsers,
  FiServer,
  FiGrid,
  FiLayers,
  FiFileText,
  FiActivity,
} from 'react-icons/fi';
import { 
  MdOutlineAttachMoney, 
  MdOutlineMoneyOff, 
  MdOutlineReceipt,
  MdOutlinePayments,
  MdOutlineAccountBalance,
  MdOutlineWork,
  MdTimeline,
} from 'react-icons/md';
import api from '../../utils/api';

// Reusable components
const StatCard = ({ title, value, icon, change, colorScheme, formatter, isLoading, footerText }) => {
  const cardBg = useColorModeValue('white', 'gray.700');
  const iconBg = useColorModeValue(`${colorScheme}.500`, `${colorScheme}.200`);
  const iconColor = useColorModeValue('white', 'gray.800');

  const displayValue = formatter ? formatter(value) : value;

  return (
    <Card boxShadow="md" bg={cardBg}>
      <CardBody p={4}>
        <Flex alignItems="center">
          <Box
            bg={iconBg}
            color={iconColor}
            p={3}
            borderRadius="lg"
            mr={4}
            boxShadow="md"
          >
            <Icon as={icon} boxSize={6} />
          </Box>
          <Box flex="1">
            <Text fontSize="sm" color="gray.500" fontWeight="medium">
              {title}
            </Text>
            {isLoading ? (
              <Spinner size="sm" mt={1} colorScheme={colorScheme} />
            ) : (
              <Text fontSize="2xl" fontWeight="bold">
                {displayValue}
              </Text>
            )}
            {change !== undefined && (
              <Stat>
                <StatHelpText mb={0}>
                  <StatArrow type={change > 0 ? 'increase' : 'decrease'} />
                  {Math.abs(change)}%
                </StatHelpText>
              </Stat>
            )}
            {footerText && (
              <Text fontSize="xs" color="gray.500" mt={1}>
                {footerText}
              </Text>
            )}
          </Box>
        </Flex>
      </CardBody>
    </Card>
  );
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const formatNumber = (value) => {
  return new Intl.NumberFormat('pt-BR').format(value);
};

// Main Dashboard Component
const FinanceiroDashboard = () => {
  const [timeFrame, setTimeFrame] = useState('month');
  const [isLoading, setIsLoading] = useState(true);
  const [finances, setFinances] = useState({
    revenue: { value: 0, change: 0 },
    expenses: { value: 0, change: 0 },
    profit: { value: 0, change: 0 },
    pendingPayments: { value: 0, change: 0 },
    transactions: { value: 0, change: 0 },
    avgValue: { value: 0, change: 0 },
  });
  const [transactions, setTransactions] = useState([]);
  const [dueBills, setDueBills] = useState([]);
  const [categories, setCategories] = useState([]);
  const [monthlyReport, setMonthlyReport] = useState({
    months: [],
    revenue: [],
    expenses: [],
  });

  useEffect(() => {
    // Simulating API call
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // In a real implementation, this would be an API call
        // const response = await api.get('api/dashboard/financeiro');
        // const data = await response.json();
        
        // Mock data for demo
        setTimeout(() => {
          setFinances({
            revenue: { value: 456789.50, change: 15 },
            expenses: { value: 287435.25, change: 8 },
            profit: { value: 169354.25, change: 22 },
            pendingPayments: { value: 42350.80, change: -12 },
            transactions: { value: 3254, change: 18 },
            avgValue: { value: 140.38, change: -3 },
          });
          
          // Mock transactions data
          setTransactions([
            { 
              id: 1, 
              description: 'Pagamento de serviços - Lote #1258', 
              type: 'receita', 
              amount: 28750.50,
              date: '29/04/2025',
              status: 'Concluído',
              category: 'Serviços',
            },
            { 
              id: 2, 
              description: 'Folha de pagamento - Abril/2025', 
              type: 'despesa', 
              amount: 45680.75,
              date: '28/04/2025',
              status: 'Concluído',
              category: 'Pessoal',
            },
            { 
              id: 3, 
              description: 'Comissões de serviços - Semana 17', 
              type: 'receita', 
              amount: 12350.00,
              date: '28/04/2025',
              status: 'Concluído',
              category: 'Comissões',
            },
            { 
              id: 4, 
              description: 'Serviços de infraestrutura AWS', 
              type: 'despesa', 
              amount: 8725.45,
              date: '27/04/2025',
              status: 'Concluído',
              category: 'TI',
            },
            { 
              id: 5, 
              description: 'Taxa de processamento de pagamentos', 
              type: 'despesa', 
              amount: 3450.80,
              date: '26/04/2025',
              status: 'Concluído',
              category: 'Taxas',
            },
          ]);
          
          // Mock bills data
          setDueBills([
            {
              id: 1,
              description: 'Renovação de licenças de software',
              amount: 12500.00,
              dueDate: '05/05/2025',
              status: 'Pendente',
              priority: 'Alta',
            },
            {
              id: 2,
              description: 'Aluguel do escritório',
              amount: 8750.00,
              dueDate: '10/05/2025',
              status: 'Pendente',
              priority: 'Alta',
            },
            {
              id: 3,
              description: 'Serviços de marketing digital',
              amount: 5680.00,
              dueDate: '15/05/2025',
              status: 'Pendente',
              priority: 'Média',
            },
            {
              id: 4,
              description: 'Consultoria jurídica mensal',
              amount: 3200.00,
              dueDate: '20/05/2025',
              status: 'Pendente',
              priority: 'Baixa',
            },
          ]);
          
          // Mock category data
          setCategories([
            { name: 'Serviços', percentage: 58, value: 265123.45, change: 18 },
            { name: 'Comissões', percentage: 22, value: 100493.69, change: 15 },
            { name: 'Assinaturas', percentage: 12, value: 54814.74, change: 8 },
            { name: 'Taxas', percentage: 8, value: 36357.62, change: 12 },
          ]);
          
          // Mock monthly report data
          setMonthlyReport({
            months: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'],
            revenue: [320000, 345000, 380000, 420000, 456000, 0],
            expenses: [220000, 235000, 260000, 275000, 287000, 0],
          });
          
          setIsLoading(false);
        }, 1500);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [timeFrame]);

  const handleTimeFrameChange = (e) => {
    setTimeFrame(e.target.value);
  };

  const cardBg = useColorModeValue('white', 'gray.700');
  const tableBg = useColorModeValue('gray.50', 'gray.700');
  const lightBg = useColorModeValue('gray.50', 'gray.600');
  
  // Status badge colors
  const getStatusColor = (status) => {
    switch (status) {
      case 'Concluído':
        return 'green';
      case 'Pendente':
        return 'yellow';
      case 'Atrasado':
        return 'red';
      case 'Agendado':
        return 'blue';
      default:
        return 'gray';
    }
  };
  
  // Transaction type colors
  const getTypeColor = (type) => {
    return type === 'receita' ? 'green' : 'red';
  };
  
  // Priority colors
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Alta':
        return 'red';
      case 'Média':
        return 'orange';
      case 'Baixa':
        return 'green';
      default:
        return 'gray';
    }
  };

  return (
    <Box w="100%" p={4}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg">Dashboard Financeiro</Heading>
          <Text color="gray.500">Análise de receitas, despesas e fluxo de caixa</Text>
        </Box>
        <HStack>
          <Text mr={2}>Período:</Text>
          <Select value={timeFrame} onChange={handleTimeFrameChange} w="150px">
            <option value="day">Hoje</option>
            <option value="week">Esta Semana</option>
            <option value="month">Este Mês</option>
            <option value="quarter">Este Trimestre</option>
            <option value="year">Este Ano</option>
          </Select>
          <Button 
            leftIcon={<FiRefreshCw />} 
            colorScheme="yellow" 
            variant="outline"
            onClick={() => {
              setIsLoading(true);
              setTimeout(() => setIsLoading(false), 1000);
            }}
          >
            Atualizar
          </Button>
        </HStack>
      </Flex>

      {/* Financial Overview Cards */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(3, 1fr)" }} gap={4} mb={6}>
        <StatCard 
          title="Receita Total" 
          value={finances.revenue.value} 
          icon={FiArrowUpRight} 
          change={finances.revenue.change} 
          colorScheme="green" 
          formatter={formatCurrency}
          isLoading={isLoading} 
          footerText="Entradas no período"
        />
        <StatCard 
          title="Despesas Totais" 
          value={finances.expenses.value} 
          icon={FiArrowDownRight} 
          change={finances.expenses.change} 
          colorScheme="red" 
          formatter={formatCurrency}
          isLoading={isLoading}
          footerText="Saídas no período"
        />
        <StatCard 
          title="Lucro Líquido" 
          value={finances.profit.value} 
          icon={MdOutlineAttachMoney} 
          change={finances.profit.change} 
          colorScheme="teal" 
          formatter={formatCurrency}
          isLoading={isLoading}
          footerText="Receita - Despesas"
        />
      </Grid>

      <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(3, 1fr)" }} gap={4} mb={6}>
        <StatCard 
          title="Pagamentos Pendentes" 
          value={finances.pendingPayments.value} 
          icon={FiClock} 
          change={finances.pendingPayments.change} 
          colorScheme="yellow" 
          formatter={formatCurrency}
          isLoading={isLoading}
          footerText="A receber no próximo período"
        />
        <StatCard 
          title="Transações Processadas" 
          value={finances.transactions.value} 
          icon={MdOutlineReceipt} 
          change={finances.transactions.change} 
          colorScheme="blue" 
          formatter={formatNumber}
          isLoading={isLoading}
          footerText="Total de operações"
        />
        <StatCard 
          title="Valor Médio por Transação" 
          value={finances.avgValue.value} 
          icon={FiBarChart2} 
          change={finances.avgValue.change} 
          colorScheme="purple" 
          formatter={formatCurrency}
          isLoading={isLoading}
          footerText="Ticket médio"
        />
      </Grid>

      {/* Financial Report & Insights */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", lg: "3fr 2fr" }} gap={6} mb={6}>
        <Card bg={cardBg} boxShadow="md">
          <CardHeader pb={2}>
            <Flex justify="space-between" align="center">
              <Heading size="md">Relatório Financeiro</Heading>
              <HStack>
                <Icon as={FiPieChart} color="blue.500" />
                <Icon as={FiBarChart2} color="green.500" ml={2} />
              </HStack>
            </Flex>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Tabs colorScheme="yellow" isFitted>
                <TabList>
                  <Tab><Icon as={FiBarChart2} mr={2} /> Receitas vs Despesas</Tab>
                  <Tab><Icon as={FiPieChart} mr={2} /> Distribuição</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel px={0}>
                    <Box mb={6}>
                      <Text fontSize="sm" color="gray.500" mb={4} textAlign="center">
                        Comparativo de Receitas e Despesas (últimos 6 meses)
                      </Text>
                      
                      {/* Placeholder for chart - in a real app, use Chart.js or Recharts */}
                      <Box h="250px" position="relative">
                        <Flex direction="column" h="100%" justify="flex-end">
                          <Flex h="100%" align="flex-end">
                            {monthlyReport.months.map((month, index) => (
                              <Flex 
                                key={index} 
                                direction="column" 
                                h="100%" 
                                flex="1" 
                                align="center" 
                                justify="flex-end"
                                px={1}
                              >
                                <Box
                                  w="100%"
                                  h={`${(monthlyReport.revenue[index] / 500000) * 100}%`}
                                  bg="green.300"
                                  borderTopRadius="md"
                                />
                                <Box
                                  w="100%"
                                  h={`${(monthlyReport.expenses[index] / 500000) * 100}%`}
                                  bg="red.300"
                                  borderTopRadius="md"
                                  mt={1}
                                />
                                <Text fontSize="xs" mt={2} fontWeight="medium">
                                  {month}
                                </Text>
                              </Flex>
                            ))}
                          </Flex>
                        </Flex>
                      </Box>
                      
                      <HStack mt={4} spacing={6} justify="center">
                        <HStack>
                          <Box w="12px" h="12px" bg="green.300" borderRadius="sm" />
                          <Text fontSize="sm">Receitas</Text>
                        </HStack>
                        <HStack>
                          <Box w="12px" h="12px" bg="red.300" borderRadius="sm" />
                          <Text fontSize="sm">Despesas</Text>
                        </HStack>
                      </HStack>
                    </Box>

                    <Divider my={4} />

                    <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(3, 1fr)" }} gap={4}>
                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={1}>Margem de Lucro</Text>
                        <CircularProgress value={37} size="100px" thickness="8px" color="teal.400">
                          <CircularProgressLabel>37%</CircularProgressLabel>
                        </CircularProgress>
                        <Text fontSize="xs" color="gray.500" mt={2}>
                          {formatCurrency(finances.profit.value)} de lucro líquido
                        </Text>
                      </Box>
                      
                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={1}>Taxa de Crescimento</Text>
                        <CircularProgress value={15} size="100px" thickness="8px" color="blue.400">
                          <CircularProgressLabel>15%</CircularProgressLabel>
                        </CircularProgress>
                        <Text fontSize="xs" color="gray.500" mt={2}>
                          Comparado ao período anterior
                        </Text>
                      </Box>
                      
                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={1}>Eficiência Operacional</Text>
                        <CircularProgress value={82} size="100px" thickness="8px" color="purple.400">
                          <CircularProgressLabel>82%</CircularProgressLabel>
                        </CircularProgress>
                        <Text fontSize="xs" color="gray.500" mt={2}>
                          Base: despesas vs receita
                        </Text>
                      </Box>
                    </Grid>
                  </TabPanel>
                  
                  <TabPanel>
                    <Text fontSize="sm" color="gray.500" mb={4} textAlign="center">
                      Distribuição de Receitas por Categoria
                    </Text>
                    
                    {/* Placeholder for pie chart - in a real app, use Chart.js or Recharts */}
                    <Stack spacing={4} maxW="600px" mx="auto">
                      {categories.map((category, index) => (
                        <Box key={index}>
                          <Flex justify="space-between" mb={1}>
                            <Text fontSize="sm" fontWeight="medium">{category.name}</Text>
                            <HStack>
                              <Text fontSize="sm" fontWeight="medium">{category.percentage}%</Text>
                              <Badge colorScheme={category.change > 0 ? 'green' : 'red'}>
                                {category.change > 0 ? `+${category.change}%` : `${category.change}%`}
                              </Badge>
                            </HStack>
                          </Flex>
                          <Flex align="center">
                            <Progress 
                              value={category.percentage} 
                              size="sm" 
                              flex="1" 
                              colorScheme={
                                index === 0 ? 'blue' :
                                index === 1 ? 'green' :
                                index === 2 ? 'purple' : 'yellow'
                              }
                              borderRadius="full"
                            />
                            <Text fontSize="sm" ml={4} w="120px">
                              {formatCurrency(category.value)}
                            </Text>
                          </Flex>
                        </Box>
                      ))}
                    </Stack>
                    
                    <HStack mt={6} spacing={4} justify="center">
                      <Box>
                        <Text fontWeight="bold" fontSize="lg">
                          {formatCurrency(finances.revenue.value)}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          Receita Total
                        </Text>
                      </Box>
                      <Divider orientation="vertical" h="40px" />
                      <Box>
                        <Text fontWeight="bold" fontSize="lg">
                          4
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          Categorias
                        </Text>
                      </Box>
                      <Divider orientation="vertical" h="40px" />
                      <Box>
                        <Text fontWeight="bold" fontSize="lg">
                          15%
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          Crescimento
                        </Text>
                      </Box>
                    </HStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            )}
          </CardBody>
        </Card>

        <Card bg={cardBg} boxShadow="md">
          <CardHeader pb={2}>
            <Heading size="md">Contas a Pagar</Heading>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Stack spacing={4}>
                {dueBills.map(bill => (
                  <Box 
                    key={bill.id} 
                    p={3} 
                    borderRadius="md"
                    bg={lightBg}
                    borderLeft="4px solid" 
                    borderLeftColor={getPriorityColor(bill.priority) + '.500'}
                  >
                    <Flex justify="space-between" align="center" mb={2}>
                      <Text fontWeight="medium" noOfLines={1}>{bill.description}</Text>
                      <Badge colorScheme={getPriorityColor(bill.priority)}>
                        {bill.priority}
                      </Badge>
                    </Flex>
                    <Flex justify="space-between" align="center">
                      <HStack spacing={4} fontSize="sm" color="gray.500">
                        <Text>Vencimento: {bill.dueDate}</Text>
                        <Badge>{bill.status}</Badge>
                      </HStack>
                      <Text fontWeight="bold" color="red.500">
                        {formatCurrency(bill.amount)}
                      </Text>
                    </Flex>
                  </Box>
                ))}
                
                <Divider />
                
                <Box>
                  <Flex justify="space-between" mb={2}>
                    <Text fontSize="sm" fontWeight="medium">Total a Pagar (Maio)</Text>
                    <Text fontWeight="bold">{formatCurrency(30130)}</Text>
                  </Flex>
                  <Flex justify="space-between" fontSize="sm" color="gray.500">
                    <Text>4 contas a pagar</Text>
                    <Text>Próximo vencimento: 05/05/2025</Text>
                  </Flex>
                </Box>
                
                <Button 
                  colorScheme="yellow" 
                  variant="outline" 
                  size="sm" 
                  mt={2} 
                  leftIcon={<FiFileText />}
                >
                  Ver Todas as Contas
                </Button>
              </Stack>
            )}
          </CardBody>
        </Card>
      </Grid>

      {/* Recent Transactions */}
      <Card bg={cardBg} boxShadow="md" mb={6}>
        <CardHeader pb={2}>
          <Flex justify="space-between" align="center">
            <Heading size="md">Transações Recentes</Heading>
            <Badge colorScheme="blue" px={3} py={1} borderRadius="full">
              {isLoading ? '-' : transactions.length} transações
            </Badge>
          </Flex>
        </CardHeader>
        <CardBody px={{ base: 2, md: 4 }}>
          {isLoading ? (
            <Flex justify="center" py={6}>
              <Spinner />
            </Flex>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead bg={tableBg}>
                  <Tr>
                    <Th>Descrição</Th>
                    <Th>Categoria</Th>
                    <Th>Data</Th>
                    <Th>Status</Th>
                    <Th isNumeric>Valor</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {transactions.map(transaction => (
                    <Tr key={transaction.id}>
                      <Td fontWeight="medium">{transaction.description}</Td>
                      <Td>{transaction.category}</Td>
                      <Td>{transaction.date}</Td>
                      <Td>
                        <Badge colorScheme={getStatusColor(transaction.status)}>
                          {transaction.status}
                        </Badge>
                      </Td>
                      <Td isNumeric>
                        <Text 
                          fontWeight="bold" 
                          color={transaction.type === 'receita' ? 'green.500' : 'red.500'}
                        >
                          {transaction.type === 'receita' ? '+ ' : '- '}
                          {formatCurrency(transaction.amount)}
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
          <Flex justify="center" mt={4}>
            <Button 
              leftIcon={<FiActivity />} 
              colorScheme="yellow" 
              variant="outline" 
              size="sm"
            >
              Ver Histórico Completo
            </Button>
          </Flex>
        </CardBody>
      </Card>

      {/* Financial Insights & Quick Stats */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(3, 1fr)" }} gap={6}>
        <Card bg={cardBg} boxShadow="md">
          <CardHeader pb={2}>
            <Flex align="center">
              <Icon as={FiTrendingUp} mr={2} />
              <Heading size="md">Projeções</Heading>
            </Flex>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Stack spacing={4}>
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={1}>Receita Projetada (Maio)</Text>
                  <Flex align="center">
                    <Text fontSize="xl" fontWeight="bold" mr={2}>
                      {formatCurrency(480000)}
                    </Text>
                    <Badge colorScheme="green">+5.3%</Badge>
                  </Flex>
                </Box>
                
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={1}>Despesas Projetadas (Maio)</Text>
                  <Flex align="center">
                    <Text fontSize="xl" fontWeight="bold" mr={2}>
                      {formatCurrency(305000)}
                    </Text>
                    <Badge colorScheme="red">+6.2%</Badge>
                  </Flex>
                </Box>
                
                <Divider />
                
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={1}>Lucro Projetado (Maio)</Text>
                  <Flex align="center">
                    <Text fontSize="xl" fontWeight="bold" color="green.500" mr={2}>
                      {formatCurrency(175000)}
                    </Text>
                    <Badge colorScheme="green">+3.4%</Badge>
                  </Flex>
                </Box>
                
                <Text fontSize="xs" color="gray.500">
                  Projeções baseadas nos dados históricos e tendências atuais
                </Text>
              </Stack>
            )}
          </CardBody>
        </Card>
        
        <Card bg={cardBg} boxShadow="md">
          <CardHeader pb={2}>
            <Flex align="center">
              <Icon as={FiTarget} mr={2} />
              <Heading size="md">Metas Financeiras</Heading>
            </Flex>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Stack spacing={4}>
                <Box>
                  <Flex justify="space-between" mb={1}>
                    <Text fontSize="sm" fontWeight="medium">Crescimento de Receita</Text>
                    <Text fontSize="sm">75% completo</Text>
                  </Flex>
                  <Progress value={75} size="sm" colorScheme="green" borderRadius="full" />
                  <Flex justify="space-between" fontSize="xs" color="gray.500" mt={1}>
                    <Text>Meta: {formatCurrency(500000)}</Text>
                    <Text>Atual: {formatCurrency(375000)}</Text>
                  </Flex>
                </Box>
                
                <Box>
                  <Flex justify="space-between" mb={1}>
                    <Text fontSize="sm" fontWeight="medium">Redução de Custos</Text>
                    <Text fontSize="sm">60% completo</Text>
                  </Flex>
                  <Progress value={60} size="sm" colorScheme="red" borderRadius="full" />
                  <Flex justify="space-between" fontSize="xs" color="gray.500" mt={1}>
                    <Text>Meta: -8%</Text>
                    <Text>Atual: -4.8%</Text>
                  </Flex>
                </Box>
                
                <Box>
                  <Flex justify="space-between" mb={1}>
                    <Text fontSize="sm" fontWeight="medium">Margem de Lucro</Text>
                    <Text fontSize="sm">85% completo</Text>
                  </Flex>
                  <Progress value={85} size="sm" colorScheme="blue" borderRadius="full" />
                  <Flex justify="space-between" fontSize="xs" color="gray.500" mt={1}>
                    <Text>Meta: 40%</Text>
                    <Text>Atual: 37%</Text>
                  </Flex>
                </Box>
              </Stack>
            )}
          </CardBody>
        </Card>
        
        <Card bg={cardBg} boxShadow="md">
          <CardHeader pb={2}>
            <Flex align="center">
              <Icon as={MdOutlineAccountBalance} mr={2} />
              <Heading size="md">Resumo Bancário</Heading>
            </Flex>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Stack spacing={4}>
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={1}>Saldo em Conta Principal</Text>
                  <Flex align="center">
                    <Text fontSize="xl" fontWeight="bold" mr={2}>
                      {formatCurrency(285630.42)}
                    </Text>
                  </Flex>
                  <Text fontSize="xs" color="gray.500">
                    Atualizado em 01/05/2025 às 08:30
                  </Text>
                </Box>
                
                <Divider />
                
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>Contas Vinculadas</Text>
                  <Stack spacing={2}>
                    <Flex justify="space-between">
                      <Text fontSize="sm">Conta Reserva</Text>
                      <Text fontSize="sm" fontWeight="medium">{formatCurrency(150000)}</Text>
                    </Flex>
                    <Flex justify="space-between">
                      <Text fontSize="sm">Conta Investimentos</Text>
                      <Text fontSize="sm" fontWeight="medium">{formatCurrency(320000)}</Text>
                    </Flex>
                    <Flex justify="space-between">
                      <Text fontSize="sm">Conta Operacional</Text>
                      <Text fontSize="sm" fontWeight="medium">{formatCurrency(85000)}</Text>
                    </Flex>
                  </Stack>
                </Box>
                
                <Text fontSize="xs" color="gray.500">
                  Total em contas: {formatCurrency(840630.42)}
                </Text>
              </Stack>
            )}
          </CardBody>
        </Card>
      </Grid>
    </Box>
  );
};

export default FinanceiroDashboard;